import React, { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Plus, Copy, ExternalLink, Pencil, Trash2, Link2, AlertTriangle } from "lucide-react";

const EMPTY = {
    name: "", url: "", company_name: "",
    address_line1: "", address_line2: "", postal_code: "", city: "", country: "Deutschland",
    contact_email: "", contact_phone: "", logo_url: "", brand_color: "#0b905d",
    language: "de", vat_id: "", notify_new_withdrawal: true, withdrawal_period_days: 14, allow_attachments: true, header_text_dark: false,
};

export default function Shops() {
    const { t } = useI18n();
    const ctx = useOutletContext() || {};
    const subscription = ctx.subscription || null;
    const subscriptionLoaded = subscription !== null;
    const hasActiveSub = !!(subscription?.plan_id && subscription?.subscription_status === "active");
    const noSubscription = subscriptionLoaded && !hasActiveSub;

    const shopLimit = hasActiveSub ? (subscription?.limits?.shops ?? 1) : 0;
    const shopUsage = subscription?.usage?.shops ?? 0;
    const atShopLimit = hasActiveSub && shopLimit !== -1 && shopUsage >= shopLimit;
    const canAdd = hasActiveSub && !atShopLimit;

    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/shops");
            setShops(data);
        } catch (e) {
            toast.error(t("toast_load_shops_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (s) => {
        setEditing(s);
        setForm({ ...EMPTY, ...s });
        setOpen(true);
    };
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            // Clean empty optional fields
            Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
            payload.name = form.name; // name required
            if (editing) {
                await api.put(`/shops/${editing.id}`, payload);
                toast.success(t("toast_shop_updated"));
            } else {
                await api.post("/shops", payload);
                toast.success(t("toast_shop_created"));
            }
            setOpen(false);
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail === "subscription_required") {
                toast.error(t("error_subscription_required"));
            } else if (detail === "shop_limit_reached") {
                toast.error(t("error_shop_limit_reached"));
            } else {
                toast.error(detail || t("toast_save_failed"));
            }
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/shops/${confirmDelete.id}`);
            toast.success(t("toast_shop_deleted"));
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(t("toast_delete_failed"));
        }
    };

    const publicUrl = (slug) => `${window.location.origin}/w/${slug}`;
    const copyLink = async (slug) => {
        const url = publicUrl(slug);
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success(t("copied"));
        } catch {
            toast.error(url);
        }
    };

    return (
        <div data-testid="shops-page">
            {/* Banner: existing shops but no subscription */}
            {noSubscription && !loading && shops.length > 0 && (
                <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-amber-900 text-sm">{t("billing_existing_no_sub_title")}</div>
                        <div className="text-amber-800 text-sm mt-0.5">{t("billing_existing_no_sub_desc")}</div>
                    </div>
                    <Link to="/app/billing" className="shrink-0 text-xs font-semibold text-amber-900 bg-amber-100 border border-amber-300 px-3 py-1.5 rounded-md hover:bg-amber-200 transition-colors whitespace-nowrap">
                        {t("billing_subscribe_now")}
                    </Link>
                </div>
            )}

            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{t("shops_title")}</h1>
                    <p className="text-gray-600 mt-1">{t("shops_sub")}</p>
                </div>
                <div className="flex items-center gap-3">
                    {atShopLimit && (
                        <Link to="/app/billing" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md font-medium hover:bg-amber-100 transition-colors">
                            {t("billing_upgrade_hint")}
                        </Link>
                    )}
                    <Button onClick={openCreate} disabled={!canAdd} data-testid="btn-add-shop" className="bg-[#0b905d] hover:bg-[#067a4d] disabled:opacity-50 disabled:cursor-not-allowed">
                        <Plus className="w-4 h-4 mr-2" /> {t("shop_add")}
                        {hasActiveSub && shopLimit !== -1 && <span className="ml-2 text-xs opacity-70 font-mono">{shopUsage}/{shopLimit}</span>}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-gray-500">{t("loading")}</div>
            ) : shops.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-md p-12 text-center bg-white" data-testid="shops-empty">
                    {noSubscription ? (
                        <>
                            <div className="font-display text-xl font-semibold text-[#00153f]">{t("billing_subscription_required")}</div>
                            <p className="text-gray-500 mt-2 max-w-sm mx-auto">{t("billing_subscription_required_desc")}</p>
                            <Link to="/app/billing" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0b905d] hover:bg-[#067a4d] text-white text-sm font-medium transition-colors">
                                {t("billing_subscribe_now")}
                            </Link>
                        </>
                    ) : (
                        <>
                            <div className="font-display text-xl font-semibold text-[#00153f]">{t("shop_no_shops_title")}</div>
                            <p className="text-gray-500 mt-2 max-w-sm mx-auto">{t("shop_no_shops_sub")}</p>
                            <Button onClick={openCreate} disabled={!canAdd} className="mt-6 bg-[#0b905d] hover:bg-[#067a4d] disabled:opacity-50 disabled:cursor-not-allowed">
                                <Plus className="w-4 h-4 mr-2" /> {t("shop_add")}
                            </Button>
                        </>
                    )}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {shops.map((s) => (
                        <div key={s.id} data-testid={`shop-card-${s.id}`} className="border border-gray-200 rounded-md bg-white p-5 card-lift">
                            <div className="flex items-start gap-3">
                                {s.logo_url ? (
                                    <img src={s.logo_url} alt="" className="h-10 w-10 rounded-md object-cover border border-gray-200" />
                                ) : (
                                    <div className="h-10 w-10 rounded-md flex items-center justify-center text-white font-display font-bold" style={{ background: s.brand_color || "#0b905d" }}>
                                        {s.name.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold text-[#00153f] truncate">{s.name}</div>
                                    {s.url && (
                                        <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-[#00153f] inline-flex items-center gap-1 truncate">
                                            {s.url} <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 dotted-divider" />

                            <div className="mt-4 text-xs text-gray-500 font-mono uppercase tracking-widest">{t("shop_link")}</div>
                            <div className="mt-1 flex items-center gap-2">
                                <div className="flex-1 min-w-0 text-sm text-[#00153f] truncate font-mono">
                                    <Link2 className="inline w-3.5 h-3.5 mr-1 text-gray-400" />
                                    /w/{s.slug}
                                </div>
                                <Button size="sm" variant="outline" onClick={() => copyLink(s.slug)} data-testid={`btn-copy-link-${s.id}`}>
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <a href={publicUrl(s.slug)} target="_blank" rel="noreferrer">
                                    <Button size="sm" variant="outline">
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                </a>
                            </div>

                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-xs text-gray-500 font-mono uppercase tracking-widest">{s.language?.toUpperCase()}</span>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`btn-edit-shop-${s.id}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(s)} data-testid={`btn-delete-shop-${s.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="shop-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? t("edit") : t("shop_add")}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1 px-6 py-4">
                        <form id="shop-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_name")} *</Label>
                                <Input data-testid="shop-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_url")}</Label>
                                <Input data-testid="shop-url" type="url" placeholder="https://..." value={form.url || ""} onChange={(e) => setField("url", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_company")}</Label>
                                <Input data-testid="shop-company" value={form.company_name || ""} onChange={(e) => setField("company_name", e.target.value)} className="mt-2" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_address1")}</Label>
                                <Input data-testid="shop-address1" value={form.address_line1 || ""} onChange={(e) => setField("address_line1", e.target.value)} className="mt-2" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_address2")}</Label>
                                <Input data-testid="shop-address2" value={form.address_line2 || ""} onChange={(e) => setField("address_line2", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_zip")}</Label>
                                <Input data-testid="shop-zip" value={form.postal_code || ""} onChange={(e) => setField("postal_code", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_city")}</Label>
                                <Input data-testid="shop-city" value={form.city || ""} onChange={(e) => setField("city", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_country")}</Label>
                                <Input data-testid="shop-country" value={form.country || ""} onChange={(e) => setField("country", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_email")}</Label>
                                <Input data-testid="shop-email" type="email" value={form.contact_email || ""} onChange={(e) => setField("contact_email", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_phone")}</Label>
                                <Input data-testid="shop-phone" value={form.contact_phone || ""} onChange={(e) => setField("contact_phone", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_vat")}</Label>
                                <Input data-testid="shop-vat" value={form.vat_id || ""} onChange={(e) => setField("vat_id", e.target.value)} className="mt-2" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_logo")}</Label>
                                <Input data-testid="shop-logo" type="url" placeholder="https://..." value={form.logo_url || ""} onChange={(e) => setField("logo_url", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_color")}</Label>
                                <div className="mt-2 flex items-center gap-2">
                                    <Input type="color" data-testid="shop-color" value={form.brand_color || "#0b905d"} onChange={(e) => setField("brand_color", e.target.value)} className="w-14 h-10 p-1" />
                                    <Input value={form.brand_color || ""} onChange={(e) => setField("brand_color", e.target.value)} className="flex-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_language")}</Label>
                                <Select value={form.language || "de"} onValueChange={(v) => setField("language", v)}>
                                    <SelectTrigger data-testid="shop-language" className="mt-2"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="de">Deutsch</SelectItem>
                                        <SelectItem value="en">English</SelectItem>
                                        <SelectItem value="fr">Français</SelectItem>
                                        <SelectItem value="es">Español</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("shop_withdrawal_period")}</Label>
                                <div className="mt-2 flex items-center gap-2">
                                    <Input
                                        data-testid="shop-withdrawal-days"
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={form.withdrawal_period_days ?? 14}
                                        onChange={(e) => setField("withdrawal_period_days", parseInt(e.target.value, 10) || 14)}
                                        className="w-24"
                                    />
                                    <span className="text-sm text-gray-500">{t("shop_withdrawal_period_unit")}</span>
                                </div>
                            </div>
                            <div className="col-span-2 flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                                <div>
                                    <div className="text-sm font-medium text-[#00153f]">{t("shop_notify_new_withdrawal")}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{t("shop_notify_new_withdrawal_desc")}</div>
                                </div>
                                <Switch
                                    checked={!!form.notify_new_withdrawal}
                                    onCheckedChange={(v) => setField("notify_new_withdrawal", v)}
                                    data-testid="shop-notify-switch"
                                />
                            </div>
                            <div className="col-span-2 flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                                <div>
                                    <div className="text-sm font-medium text-[#00153f]">{t("shop_allow_attachments")}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{t("shop_allow_attachments_desc")}</div>
                                </div>
                                <Switch
                                    checked={form.allow_attachments !== false}
                                    onCheckedChange={(v) => setField("allow_attachments", v)}
                                    data-testid="shop-attachments-switch"
                                />
                            </div>
                            <div className="col-span-2 flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                                <div>
                                    <div className="text-sm font-medium text-[#00153f]">{t("shop_header_text_dark")}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{t("shop_header_text_dark_desc")}</div>
                                </div>
                                <Switch
                                    checked={!!form.header_text_dark}
                                    onCheckedChange={(v) => setField("header_text_dark", v)}
                                    data-testid="shop-header-text-dark-switch"
                                />
                            </div>
                        </form>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                        <Button type="submit" form="shop-form" data-testid="shop-submit" className="bg-[#0b905d] hover:bg-[#067a4d]">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("shop_delete_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("shop_delete_desc", { name: confirmDelete?.name || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-shop">{t("delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
